import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { AnimatePresence, motion } from "framer-motion";

import LoadingBricks from "UI/FnrLoadingBricks2";

export default function SquareCheck({
  checked = true,
  checkboxSize = 22,
  color,
  role,
  loading,
  ariaChecked,
  ariaLabelledBy,
  className,
  circle = false,
}) {
  const [width, height, stroke] = [8, 14, 3];

  const [changeCount, setChangeCount] = useState(-1);

  const [showLoading, setShowLoading] = useState(loading);
  const [checkShowLoadingTrigger, setCheckShowLoadingTrigger] = useState(0);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return setIsMounted(false);
  }, []);

  useEffect(() => {
    setChangeCount((currentVal) => currentVal + 1);
  }, [checked]);

  const isAnimatingCheckmarkRef = useRef(false);
  const checkboxAnimationStarted = () => {
    isAnimatingCheckmarkRef.current = true;
  };
  const checkboxAnimationStopped = () => {
    isAnimatingCheckmarkRef.current = false;
    setTimeout(() => {
      if (isMounted) setCheckShowLoadingTrigger((currentVal) => currentVal + 1);
    }, 500);
  };

  useEffect(() => {
    if (!loading || isAnimatingCheckmarkRef.current)
      return setShowLoading(false);

    if (loading) setShowLoading(true);
  }, [loading, checkShowLoadingTrigger]);

  return (
    <Container
      data-cy='FnrEnrichedCheckbox_SquareCheck'
      className={`${className ? className : ""} ${checked ? "on" : ""} ${
        circle ? "circle" : ""
      }`}
      $checkboxSize={checkboxSize}
      initial={{ scale: 1 }}
      animate={{
        scale: changeCount > 0 && checked ? [1, 1.1, 1] : 1,
        transition: { delay: 0.22, duration: 0.2 },
      }}
      role={role}
      aria-checked={ariaChecked}
      aria-labelledby={ariaLabelledBy}
    >
      <Background />
      <CheckedBackground color={color} />
      <Border color={color} />
      <LoadingBricksHolder>
        <LoadingBricks
          loading={showLoading}
          color={checked ? "#FFFFFF" : "#828c8d"}
        >
          <LoadingBricksPlaceholder $checkboxSize={checkboxSize} />
          <CheckMarkOverflow>
            <CheckMarkHolder>
              {width && height && stroke && (
                <AnimatePresence initial={false}>
                  {checked && (
                    <>
                      <CheckLine1
                        key="check1"
                        style={{ height: stroke }}
                        initial={{ width: stroke }}
                        animate={{
                          width: [stroke, width, width],
                          transition: { duration: 0.3 },
                        }}
                        exit={{
                          width: [width, stroke, stroke],
                          x: [0, width - stroke, width - stroke],
                          y: [0, 0, 0 - height * 1.2],
                          transition: { duration: 0.3 },
                        }}
                        onAnimationStart={checkboxAnimationStarted}
                      ></CheckLine1>
                      <CheckLine2
                        key="check2"
                        style={{ width: stroke }}
                        initial={{ height: stroke, x: 0 }}
                        animate={{
                          x: [0, width - stroke, width - stroke],
                          height: [stroke, stroke, height],
                          transition: { duration: 0.3 },
                        }}
                        exit={{
                          y: [0, 0, 0 - height * 1.2],
                          transition: { duration: 0.3 },
                        }}
                        onAnimationComplete={checkboxAnimationStopped}
                      />
                    </>
                  )}
                </AnimatePresence>
              )}
            </CheckMarkHolder>
          </CheckMarkOverflow>
        </LoadingBricks>
      </LoadingBricksHolder>
    </Container>
  );
}
const Container = styled(motion.div)`
  width: ${(props) => props.$checkboxSize}px;
  height: ${(props) => props.$checkboxSize}px;
  cursor: pointer;
  position: relative;

  &.disabled {
    cursor: not-allowed;
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 6px;
  overflow: hidden;
  position: absolute;
  background: radial-gradient(circle at bottom right, #fff, #eceff0);
  z-index: 1;
  transition: background 0.2s;
  &::after {
    content: "";
    display: block;
    inset: 0;
    position: absolute;
    background: rgba(0, 0, 0, 0.15);
    z-index: 2;
    opacity: 0;
    transition: opacity 1s;
    ${Container}:not(.disabled):active & {
      opacity: 1;
      transition: opacity 0.2s;
    }
  }
  ${Container}.circle & {
    border-radius: 40px;
  }
`;
const CheckedBackground = styled(Background)`
  background: ${(props) =>
    props.color
      ? props.color
      : "radial-gradient(circle at top left, #016099, #1476ad)"};
  opacity: 0;
  transition: opacity 0.2s;
  transition-delay: 0.2s;
  z-index: 4;
  ${Container}.on & {
    opacity: 1;
    transition-delay: 0s;
  }
`;
const Border = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  border-radius: 6px;
  border: 2px solid #596a70;
  transition: border 0.2s;
  ${Container}:not(.on):hover & {
    border: 2px solid #278dc1;
  }
  ${Container}.disabled:hover & {
    border-color: #596a70;
  }
  ${Container}.circle & {
    border-radius: 40px;
  }
`;
const CheckMarkOverflow = styled.div`
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  z-index: 100;
  border-radius: 4px;
  ${Container}.circle & {
    border-radius: 40px;
  }
`;
const CheckMarkHolder = styled.div`
  width: 8px;
  height: 14px;
  position: relative;
  z-index: 100;
  margin-top: -10%;
  margin-right: -5%;
  transform: rotate(45deg);
`;
const CheckLine1 = styled(motion.div)`
  background: #eceff0;
  left: 0;
  bottom: 0;
  position: absolute;
  width: 100%;
  border-radius: 40px;
`;
const CheckLine2 = styled(motion.div)`
  background: #eceff0;
  left: 0;
  bottom: 0;
  height: 100%;
  position: absolute;
  border-radius: 40px;
`;
const LoadingBricksHolder = styled.div`
  position: absolute;
  z-index: 500;
  inset: 0;
`;
const LoadingBricksPlaceholder = styled.div`
  width: ${(props) => props.$checkboxSize}px;
  height: ${(props) => props.$checkboxSize}px;
`;
